import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal as AntModal, Input as AntInput, InputNumber as AntInputNumber, Button as AntButton, message } from 'antd';
import { HomeOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { generatePdfBlobFromElement } from '../utils/pdfExportUtil';

/**
 * RoomAssignmentModal - 룸번호 편집 모달
 * 
 * Props:
 * - open: boolean - 모달 표시 여부
 * - onClose: () => void - 모달 닫기 콜백
 * - travelers: array - 여행객 리스트 (룸번호 포함)
 * - onSave: (updatedTravelers) => Promise<void> - 저장 콜백 (전체 여행객 정보와 함께 저장)
 * - editLocked: boolean - 편집 잠금 여부
 */
const RoomAssignmentModal = ({ open, onClose, travelers = [], onSave, editLocked = false, productName, departureDate }) => {
  // 로컬 룸번호 상태 (여행객번호 -> 룸번호 매핑)
  const [roomAssignments, setRoomAssignments] = useState({});
  const [saving, setSaving] = useState(false);
  const printRef = useRef(null);

  // 모달 열릴 때 현재 룸번호 초기화
  useEffect(() => {
    if (open && travelers.length > 0) {
      const initial = {};
      travelers.forEach((t) => {
        const travelerNo = t?.여행객번호 || t?.여행객키;
        if (travelerNo) {
          initial[travelerNo] = t?.룸번호 ?? null;
        }
      });
      setRoomAssignments(initial);
    }
  }, [open, travelers]);

  // 룸번호 변경 핸들러
  const handleRoomChange = (travelerNo, value) => {
    setRoomAssignments((prev) => ({
      ...prev,
      [travelerNo]: value,
    }));
  };

  // 일괄 룸번호 설정
  const [bulkRoomNo, setBulkRoomNo] = useState(null);
  const applyBulkRoom = () => {
    if (bulkRoomNo == null) return;
    const updated = {};
    travelers.forEach((t) => {
      const travelerNo = t?.여행객번호 || t?.여행객키;
      if (travelerNo) {
        updated[travelerNo] = bulkRoomNo;
      }
    });
    setRoomAssignments(updated);
    message.success(`모든 여행객에 룸번호 ${bulkRoomNo} 적용됨`);
  };

  // 저장 핸들러
  const handleSave = async () => {
    if (editLocked) {
      message.warning('편집이 잠금 상태입니다.');
      return;
    }
    try {
      setSaving(true);
      // 여행객 리스트에 룸번호 업데이트 적용
      const updatedTravelers = travelers.map((t) => {
        const travelerNo = t?.여행객번호 || t?.여행객키;
        return {
          ...t,
          룸번호: roomAssignments[travelerNo] ?? t?.룸번호 ?? null,
        };
      });
      await onSave(updatedTravelers);
      message.success('룸번호가 저장되었습니다.');
      onClose();
    } catch (e) {
      console.error(e);
      message.error(e?.message || '룸번호 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 룸번호별 그룹핑 (미리보기)
  const roomGroups = useMemo(() => {
    const groups = {};
    travelers.forEach((t) => {
      const travelerNo = t?.여행객번호 || t?.여행객키;
      const roomNo = roomAssignments[travelerNo] ?? t?.룸번호;
      const key = roomNo != null ? String(roomNo) : '미배정';
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return groups;
  }, [travelers, roomAssignments]);

  // 취소된 여행객 제외한 리스트
  const activeTravelers = useMemo(() => {
    return travelers.filter((t) => t?.여행객종류 !== '취소');
  }, [travelers]);

  // 인쇄용 DOM 생성
  const buildPrintableElement = () => {
    const container = document.createElement('div');
    container.style.cssText = 'width: 794px; background: #ffffff; padding: 20px; box-sizing: border-box; font-family: \'Malgun Gothic\', \'맑은 고딕\', sans-serif; color: #111827;';

    const title = document.createElement('div');
    title.style.cssText = 'font-size: 18px; font-weight: 700; margin-bottom: 6px;';
    title.textContent = '룸번호 배정';
    container.appendChild(title);

    const meta = document.createElement('div');
    meta.style.cssText = 'font-size: 12px; color: #374151; margin-bottom: 12px; display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 8px;';
    const totalActive = activeTravelers.length;
    const metaItems = [
      `상품명: ${productName ? String(productName) : '-'}`,
      `출발일: ${departureDate ? String(departureDate) : '-'}`,
      `생성일: ${new Date().toLocaleString('ko-KR')} · 인원: ${totalActive}명`,
    ];
    metaItems.forEach((t) => {
      const span = document.createElement('div');
      span.textContent = t;
      meta.appendChild(span);
    });
    container.appendChild(meta);

    // 그룹 요약
    const groupWrap = document.createElement('div');
    groupWrap.style.cssText = 'padding: 10px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; margin-bottom: 16px;';
    const groupTitle = document.createElement('div');
    groupTitle.style.cssText = 'font-size: 13px; font-weight: 600; margin-bottom: 8px;';
    groupTitle.textContent = '룸별 배정 현황';
    groupWrap.appendChild(groupTitle);
    const chips = document.createElement('div');
    chips.style.cssText = 'display: flex; flex-wrap: wrap; gap: 6px;';
    Object.entries(roomGroups)
      .sort(([a],[b]) => {
        if (a === '미배정') return 1;
        if (b === '미배정') return -1;
        return Number(a) - Number(b);
      })
      .forEach(([roomNo, members]) => {
        const active = (members || []).filter(m => m?.여행객종류 !== '취소');
        if (active.length === 0) return;
        const chip = document.createElement('div');
        chip.style.cssText = `display:inline-block; padding: 4px 8px; border-radius: 6px; font-size: 12px; border: 1px solid ${roomNo==='미배정'?'#f59e0b':'#93c5fd'}; color: ${roomNo==='미배정'?'#92400e':'#1d4ed8'}; background: ${roomNo==='미배정'?'#fffbeb':'#eff6ff'};`;
        chip.textContent = `${roomNo==='미배정'?'미배정':roomNo+'호'} (${active.length}명)`;
        chips.appendChild(chip);
      });
    groupWrap.appendChild(chips);
    container.appendChild(groupWrap);

    // 명단 테이블 (룸번호 컬럼 묶기)
    const table = document.createElement('table');
    table.style.cssText = 'width: 100%; border-collapse: collapse;';
    const thead = document.createElement('thead');
    const trh = document.createElement('tr');
    const headers = ['순번','이름','성별','연령구분','영문명','룸번호'];
    headers.forEach(h => {
      const th = document.createElement('th');
      th.textContent = h;
      th.style.cssText = 'text-align:left; font-size:12px; padding:8px; border:1px solid #e5e7eb; background:#f3f4f6;';
      trh.appendChild(th);
    });
    thead.appendChild(trh);
    table.appendChild(thead);
    const tbody = document.createElement('tbody');
    // 그룹 정렬: 숫자 룸번호 오름차순, 미배정은 마지막
    const sortedGroups = Object.entries(roomGroups).sort(([a],[b]) => {
      if (a === '미배정') return 1;
      if (b === '미배정') return -1;
      return Number(a) - Number(b);
    });

    let seq = 0;
    sortedGroups.forEach(([roomNo, members]) => {
      const actives = (members || []).filter(m => m?.여행객종류 !== '취소');
      if (actives.length === 0) return;

      actives.forEach((t, idxInGroup) => {
        const tr = document.createElement('tr');
        const travelerNo = t?.여행객번호 || t?.여행객키;
        const currentRoom = roomAssignments[travelerNo] ?? t?.룸번호 ?? '';

        const cols = [
          String(++seq),
          String(t?.한글이름 || ''),
          String(t?.성별 || ''),
          String(t?.연령구분 || ''),
          (t?.영문성 && t?.영문이름) ? `${t.영문성} ${t.영문이름}` : '',
        ];
        cols.forEach(val => {
          const td = document.createElement('td');
          td.textContent = val;
          td.style.cssText = 'font-size:12px; padding:8px; border:1px solid #e5e7eb;';
          tr.appendChild(td);
        });

        // 룸번호 컬럼을 그룹 묶음으로 rowspan
        if (idxInGroup === 0) {
          const tdRoom = document.createElement('td');
          tdRoom.textContent = roomNo === '미배정' ? '미배정' : String(currentRoom || roomNo);
          tdRoom.style.cssText = 'font-size:12px; padding:8px; border:1px solid #e5e7eb; text-align:left; background:#fbfbfb;';
          tdRoom.setAttribute('rowspan', String(actives.length));
          tr.appendChild(tdRoom);
        }

        tbody.appendChild(tr);
      });
    });
    table.appendChild(tbody);
    container.appendChild(table);

    return container;
  };

  const handleOpenPrintPreview = () => {
    try {
      const el = buildPrintableElement();
      const win = window.open('', '_blank');
      if (!win) { message.warning('팝업이 차단되었습니다. 팝업 허용 후 다시 시도해주세요.'); return; }
      const titleSafe = (productName ? String(productName) : '룸번호 배정').replace(/["<>:|?*]/g, ' ');
      const today = new Date().toISOString().split('T')[0];
      const html = `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${titleSafe} - 룸번호 배정 (${today})</title>
  <style>
    html, body { height: 100%; }
    body { margin: 0; background: #f3f4f6; font-family: 'Malgun Gothic','\uB9D0\uADF8\uC740 \uACE0\uB515','Apple SD Gothic Neo',sans-serif; color: #111827; }
    .toolbar { position: sticky; top: 0; z-index: 100; background: #ffffff; border-bottom: 1px solid #e5e7eb; padding: 10px 12px; display: flex; gap: 8px; align-items: center; }
    .toolbar-title { font-weight: 600; font-size: 14px; margin-right: auto; color: #111827; }
    .btn { appearance: none; border: 1px solid #d1d5db; background: #ffffff; color: #111827; padding: 6px 10px; border-radius: 6px; font-size: 12px; cursor: pointer; }
    .btn.primary { background: #2563eb; color: #ffffff; border-color: #2563eb; }
    .sheet { width: 210mm; min-height: 297mm; margin: 12px auto; background: #ffffff; box-shadow: 0 1px 2px rgba(0,0,0,0.05), 0 1px 6px rgba(0,0,0,0.08); }
    .content { padding: 10mm; }
    /* Force injected element to fit sheet */
    .content > * { width: 100% !important; max-width: 100% !important; box-sizing: border-box; margin: 0 auto; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #e5e7eb; padding: 8px; font-size: 12px; }
    thead th { background: #f3f4f6; text-align: left; }
    @media print {
      @page { size: A4; margin: 10mm; }
      body { background: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .toolbar { display: none !important; }
      .sheet { box-shadow: none !important; margin: 0 !important; width: 210mm !important; min-height: 297mm !important; }
      .content { padding: 0 !important; }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <div class="toolbar-title">${titleSafe} · 미리보기 (${today})</div>
    <button class="btn" onclick="window.close()">닫기</button>
    <button class="btn primary" onclick="window.print()">인쇄</button>
  </div>
  <div class="sheet">
    <div class="content">${el.outerHTML}</div>
  </div>
</body>
</html>`;
      win.document.open();
      win.document.write(html);
      win.document.close();
      try { win.focus(); } catch (_) {}
    } catch (e) {
      console.error(e);
      message.error(e?.message || '인쇄 미리보기 생성에 실패했습니다.');
    }
  };

  const handlePrintPdf = async () => {
    try {
      const el = buildPrintableElement();
      const blob = await generatePdfBlobFromElement(el);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const today = new Date().toISOString().split('T')[0];
      a.href = url;
      a.download = `룸번호_배정_${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success('PDF가 생성되어 다운로드되었습니다.');
    } catch (e) {
      console.error(e);
      message.error(e?.message || 'PDF 생성에 실패했습니다.');
    }
  };

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      title={
        <div className="flex items-center gap-2">
          
          <span>룸번호 배정</span>
        </div>
      }
      width={700}
      footer={
        <div className="flex justify-between gap-2">
          <div className="flex gap-2">
            <AntButton onClick={handleOpenPrintPreview}>인쇄</AntButton>
            
          </div>
          <AntButton
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={editLocked}
            
          >
            저장
          </AntButton>
        </div>
      }
      destroyOnClose
    >
      <div className="space-y-4" ref={printRef}>
        {/* 룸별 그룹 미리보기 */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="text-xs font-medium text-gray-700 mb-2">배정 현황</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(roomGroups)
              .sort(([a], [b]) => {
                if (a === '미배정') return 1;
                if (b === '미배정') return -1;
                return Number(a) - Number(b);
              })
              .map(([roomNo, members]) => {
                const activeMembers = members.filter((m) => m?.여행객종류 !== '취소');
                if (activeMembers.length === 0) return null;
                return (
                  <div
                    key={roomNo}
                    className={`${
                      roomNo === '미배정'
                        ? 'bg-amber-100 text-amber-700 border border-amber-200'
                        : 'bg-blue-100 text-blue-700 border border-blue-200'
                    } px-2 py-1 rounded text-xs`}
                  >
                    <span className="font-medium">{roomNo === '미배정' ? '미배정' : `${roomNo}호`}</span>
                    <span className="ml-1">({activeMembers.length}명)</span>
                  </div>
                );
              })}
          </div>
        </div>

        {/* 여행객별 룸번호 편집 */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-600">
              <div className="col-span-1">순번</div>
              <div className="col-span-3">이름</div>
              <div className="col-span-1">성별</div>
              <div className="col-span-2">연령구분</div>
              <div className="col-span-3">영문명</div>
              <div className="col-span-2">룸번호</div>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {activeTravelers.map((t, idx) => {
              const travelerNo = t?.여행객번호 || t?.여행객키;
              const currentRoom = roomAssignments[travelerNo] ?? t?.룸번호;
              const isCancelled = t?.여행객종류 === '취소';
              
              return (
                <div
                  key={travelerNo || idx}
                  className={`grid grid-cols-12 gap-2 px-3 py-2 border-b border-gray-100 items-center ${
                    isCancelled ? 'bg-red-50 opacity-60' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="col-span-1 text-sm text-gray-600">{idx + 1}</div>
                  <div className="col-span-3 text-sm font-medium text-gray-900 truncate">
                    {t?.한글이름 || '(이름 없음)'}
                  </div>
                  <div className="col-span-1 text-xs text-gray-600">
                    {t?.성별 || '-'}
                  </div>
                  <div className="col-span-2 text-xs text-gray-600">
                    {t?.연령구분 || '-'}
                  </div>
                  <div className="col-span-3 text-xs text-gray-600 truncate">
                    {t?.영문성 && t?.영문이름 ? `${t.영문성} ${t.영문이름}` : '-'}
                  </div>
                  <div className="col-span-2">
                    <AntInputNumber
                      size="small"
                      placeholder="룸번호"
                      value={currentRoom}
                      onChange={(val) => handleRoomChange(travelerNo, val)}
                      min={1}
                      disabled={editLocked || isCancelled}
                      className="w-full"
                    />
                  </div>
                </div>
              );
            })}
            {activeTravelers.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-gray-500">
                여행객이 없습니다.
              </div>
            )}
          </div>
        </div>
      </div>
    </AntModal>
  );
};

export default RoomAssignmentModal;
